/**
 * NKBA API
 * The RESTful backend behind the NKBA application
 *
 * OpenAPI spec version: V1.0.0
 * Contact: cloud@nkba.org
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 */

import { AxiosRequestConfig } from 'axios';
import { axios } from '../axios.config';

import { ChangePasswordRequest } from '../models/ChangePasswordRequest';
import { ContactRequest } from '../models/ContactRequest';
import { EmailRequest } from '../models/EmailRequest';
import { Member } from '../models/Member';
import { MemberPermissions } from '../models/MemberPermissions';
import { MemberSignupRequest } from '../models/MemberSignupRequest';
import { MemberSignupResponse } from '../models/MemberSignupResponse';
import { ResultsPartialMemberInteger } from '../models/ResultsPartialMemberInteger';
import { ResultsPartialMemberObjectId } from '../models/ResultsPartialMemberObjectId';

/* tslint:disable:no-unused-variable member-ordering max-line-length */

export class MemberApiResource {
  /**
   * Reactivate an expired member
   *
   * @param memberId Member id
   */
  public activateMember(memberId: Partial<string>, axiosConfig?: AxiosRequestConfig): Promise<{}> {
    const reqPath = '/v2/member/{memberId}/activate'.replace('{' + 'memberId}', String(memberId));
    let reqConfig = {
      ...axiosConfig,
      method: 'POST',
      url: reqPath,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Change a member&#39;s password
   *
   * @param memberId Member id
   * @param body
   */
  public changePassword(
    memberId: Partial<string>,
    body?: Partial<ChangePasswordRequest>,
    axiosConfig?: AxiosRequestConfig
  ): Promise<{}> {
    const reqPath = '/v2/member/{memberId}/password'.replace('{' + 'memberId}', String(memberId));
    let reqConfig = {
      ...axiosConfig,
      method: 'POST',
      url: reqPath,
      data: body,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Create a new member
   *
   * @param body Member data to create
   */
  public createMember(body?: Partial<Member>, axiosConfig?: AxiosRequestConfig): Promise<Member> {
    const reqPath = '/v2/member';
    let reqConfig = {
      ...axiosConfig,
      method: 'POST',
      url: reqPath,
      data: body,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Export members CSV file
   *
   * @param q Search query
   */
  public exportCsv(query?: { q?: string }, axiosConfig?: AxiosRequestConfig): Promise<{}> {
    const reqPath = '/v2/member/export';
    let reqConfig = {
      ...axiosConfig,
      method: 'GET',
      url: reqPath,
      params: query,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Export dues CSV file
   *
   */
  public exportDuesCsv(axiosConfig?: AxiosRequestConfig): Promise<{}> {
    const reqPath = '/v2/member/export/dues';
    let reqConfig = {
      ...axiosConfig,
      method: 'GET',
      url: reqPath,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Export archived members CSV file
   *
   */
  public exportArchivedCsv(query?: { q?: string }, axiosConfig?: AxiosRequestConfig): Promise<{}> {
    const reqPath = '/v2/member/export/archived';
    let reqConfig = {
      ...axiosConfig,
      method: 'GET',
      url: reqPath,
      params: query,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Find members by a search query
   *
   * @param orgMemberId Org member id to search strictly within
   * @param q Search query
   * @param from Member id to start from
   * @param limit Number of results to return
   * @param ids Comma-separated list of member ids to retrieve
   */
  public findMembers(
    query?: { orgMemberId?: string; q?: string; from?: string; limit?: number; ids?: string },
    axiosConfig?: AxiosRequestConfig
  ): Promise<ResultsPartialMemberObjectId> {
    const reqPath = '/v2/member';
    let reqConfig = {
      ...axiosConfig,
      method: 'GET',
      url: reqPath,
      params: query,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  public getEligibleItemsPerOrganization(orgId: string, axiosConfig?: AxiosRequestConfig) {
    const reqPath = `/v2/eligibleproduct/${orgId}`;
    const reqConfig = {
      ...axiosConfig,
      method: 'GET',
      url: reqPath,
    };
    return axios.request(reqConfig).then(res => res.data);
  }

  public getAllProductsPerOrganization(orgId: string, axiosConfig?: AxiosRequestConfig) {
    const reqPath = `/v2/eligibleproduct/allproducts/${orgId}`;
    const reqConfig = {
      ...axiosConfig,
      method: 'GET',
      url: reqPath,
    };
    return axios.request(reqConfig).then(res => res.data);
  }
  /**
   * Find sub-org members, optionally with a geoquery
   *
   * @param parentMemberId Org member id to search strictly within
   * @param near Geoquery in the form of &#39;longitude,latitude,radius&#39; where radius is in meters
   * @param from Result number to start from
   * @param limit Number of results to return
   */
  public findSuborgs(
    parentMemberId: Partial<string>,
    query?: { near?: string; from?: number; limit?: number },
    axiosConfig?: AxiosRequestConfig
  ): Promise<ResultsPartialMemberInteger> {
    const reqPath = '/v2/member/{parentMemberId}/suborgs'.replace('{' + 'parentMemberId}', String(parentMemberId));
    let reqConfig = {
      ...axiosConfig,
      method: 'GET',
      url: reqPath,
      params: query,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Return the currently logged in member
   *
   */
  public getMe(axiosConfig?: AxiosRequestConfig): Promise<Member> {
    const reqPath = '/v2/member/me';
    let reqConfig = {
      ...axiosConfig,
      method: 'GET',
      url: reqPath,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Return the currently logged in member, with permissions
   *
   */
  public getMePermissions(axiosConfig?: AxiosRequestConfig): Promise<MemberPermissions> {
    const reqPath = '/v2/member/me/permissions';
    let reqConfig = {
      ...axiosConfig,
      method: 'GET',
      url: reqPath,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Return a particular member by id
   *
   * @param memberId Member id
   * @param expandTags Expand tags property
   * @param expandAwards Expand awards property
   */
  public getMember(
    memberId: Partial<string>,
    query?: { expandTags?: boolean; expandAwards?: boolean; archived?: boolean },
    axiosConfig?: AxiosRequestConfig
  ): Promise<Member> {
    const reqPath = '/v2/member/{memberId}'.replace('{' + 'memberId}', String(memberId));
    let reqConfig = {
      ...axiosConfig,
      method: 'GET',
      url: reqPath,
      params: query,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Return a particular member by number
   *
   * @param num Member number
   * @param expandTags Expand tags property
   * @param expandAwards Expand awards property
   */
  public getMemberByNumber(
    num: Partial<number>,
    query?: { expandTags?: boolean; expandAwards?: boolean; archived?: boolean },
    axiosConfig?: AxiosRequestConfig
  ): Promise<Member> {
    const reqPath = '/v2/member/number/{num}'.replace('{' + 'num}', String(num));
    let reqConfig = {
      ...axiosConfig,
      method: 'GET',
      url: reqPath,
      params: query,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Return a particular member by slug
   *
   * @param slug Member slug
   * @param expandTags Expand tags property
   * @param expandAwards Expand awards property
   */
  public getMemberBySlug(
    slug: Partial<string>,
    query?: { expandTags?: boolean; expandAwards?: boolean },
    axiosConfig?: AxiosRequestConfig
  ): Promise<Member> {
    const reqPath = '/v2/member/slug/{slug}'.replace('{' + 'slug}', String(slug));
    let reqConfig = {
      ...axiosConfig,
      method: 'GET',
      url: reqPath,
      params: query,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Return a person matching a particular first name / last name combination, if one exists in the org
   *
   * @param orgMemberId Member id of parent org
   * @param firstName First name
   * @param lastName Last name
   */
  public getOrgMemberByName(
    orgMemberId: Partial<string>,
    query?: { firstName?: string; lastName?: string },
    axiosConfig?: AxiosRequestConfig
  ): Promise<ResultsPartialMemberObjectId> {
    const reqPath = '/v2/member/{orgMemberId}/name'.replace('{' + 'orgMemberId}', String(orgMemberId));
    let reqConfig = {
      ...axiosConfig,
      method: 'GET',
      url: reqPath,
      params: query,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Invite a user to an organization, creating the user if the email does not exist
   *
   * @param body Member data to create or invite
   */
  public inviteUser(body?: Partial<Member>, axiosConfig?: AxiosRequestConfig): Promise<Member> {
    const reqPath = '/v2/member/invite';
    let reqConfig = {
      ...axiosConfig,
      method: 'POST',
      url: reqPath,
      data: body,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Send a contact email to the member
   *
   * @param memberId Member id
   * @param body
   */
  public sendContactEmail(
    memberId: Partial<string>,
    body?: Partial<ContactRequest>,
    axiosConfig?: AxiosRequestConfig
  ): Promise<{}> {
    const reqPath = '/v2/member/{memberId}/contact'.replace('{' + 'memberId}', String(memberId));
    let reqConfig = {
      ...axiosConfig,
      method: 'POST',
      url: reqPath,
      data: body,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Send a password reset email
   *
   * @param body User email address
   */
  public sendResetEmail(body?: Partial<EmailRequest>, axiosConfig?: AxiosRequestConfig): Promise<{}> {
    const reqPath = '/v2/member/sendreset';
    let reqConfig = {
      ...axiosConfig,
      method: 'POST',
      url: reqPath,
      data: body,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Send learndash application denied email
   *
   * @param body User email address
   */
   public sendLearndashDenial(body?: Partial<EmailRequest>, axiosConfig?: AxiosRequestConfig): Promise<{}> {
    const reqPath = '/v2/member/sendlearndashdenial';
    let reqConfig = {
      ...axiosConfig,
      method: 'POST',
      url: reqPath,
      data: body,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  public sendLearndashApproval(body?: Partial<EmailRequest>, axiosConfig?: AxiosRequestConfig): Promise<{}> {
    const reqPath = '/v2/member/sendlearndashapproval';
    let reqConfig = {
      ...axiosConfig,
      method: 'POST',
      url: reqPath,
      data: body,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Sign up for a new membership by placing an initial order
   *
   * @param body Signup information
   */
  public signupMember(
    body?: Partial<MemberSignupRequest>,
    axiosConfig?: AxiosRequestConfig
  ): Promise<MemberSignupResponse> {
    const reqPath = '/v2/member/signup';
    let reqConfig = {
      ...axiosConfig,
      method: 'POST',
      url: reqPath,
      data: body,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Update an existing member
   *
   * @param memberId Member id
   * @param body Member data to update
   */
  public updateMember(
    memberId: Partial<string>,
    body?: Partial<Member>,
    axiosConfig?: AxiosRequestConfig
  ): Promise<{}> {
    const reqPath = '/v2/member/{memberId}'.replace('{' + 'memberId}', String(memberId));
    let reqConfig = {
      ...axiosConfig,
      method: 'PATCH',
      url: reqPath,
      data: body,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Delete a member by id
   *
   * @param memberId Member id
   */
  public deleteMember(memberId: Partial<string>, axiosConfig?: AxiosRequestConfig): Promise<{}> {
    const reqPath = '/v2/member/{memberId}'.replace('{' + 'memberId}', String(memberId));
    let reqConfig = {
      ...axiosConfig,
      method: 'DELETE',
      url: reqPath,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Unarchive a member by id
   *
   * @param memberId Member id
   */
  public unarchiveMember(memberId: Partial<string>, axiosConfig?: AxiosRequestConfig): Promise<{}> {
    const reqPath = '/v2/member/{memberId}/unarchive'.replace('{' + 'memberId}', String(memberId));
    let reqConfig = {
      ...axiosConfig,
      method: 'POST',
      url: reqPath,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }

  /**
   * Permanently delete a member by id
   *
   * @param memberId Member id
   */
  public deleteMemberPermanent(memberId: Partial<string>, axiosConfig?: AxiosRequestConfig): Promise<{}> {
    const reqPath = '/v2/member/{memberId}/permanent'.replace('{' + 'memberId}', String(memberId));
    let reqConfig = {
      ...axiosConfig,
      method: 'DELETE',
      url: reqPath,
    };
    return axios.request(reqConfig).then(res => {
      return res.data;
    });
  }
}

export const MemberApi = new MemberApiResource();



// WEBPACK FOOTER //
// ./src/common/api/resources/MemberApi.ts